import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAdminProducts } from '@/api/productApi';
import { getAllSales, getMySales, type SalePage } from '@/api/saleApi';
import { getUserStatistics } from '@/features/users/api/adminUsersApi';
import type { UserStatistics } from '@/features/users/types';
import { tokenHasPermission } from '@/features/auth/utils/jwtPermissions';
import { storage } from '@/utils/storage';

export interface DashboardResource<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
}

const createResource = <T,>(): DashboardResource<T> => ({
  data: null,
  isLoading: false,
  error: null,
});

export const useDashboardData = () => {
  const { t } = useTranslation();
  const accessToken = storage.getAccessToken();
  const canCreateSale = tokenHasPermission(accessToken, 'SALE_CREATE');
  const canReadAllSales = tokenHasPermission(accessToken, 'SALE_READ_ALL');
  const canReadOwnSales = tokenHasPermission(accessToken, 'SALE_READ_OWN');
  const canReadProducts = tokenHasPermission(accessToken, 'PRODUCT_READ');
  const canReadUsers = tokenHasPermission(accessToken, 'USER_READ_ALL');
  const salesScope: 'all' | 'own' | null = canReadAllSales
    ? 'all'
    : canReadOwnSales
      ? 'own'
      : null;

  const [sales, setSales] = useState<DashboardResource<SalePage>>(createResource);
  const [productTotal, setProductTotal] = useState<DashboardResource<number>>(createResource);
  const [userStatistics, setUserStatistics] =
    useState<DashboardResource<UserStatistics>>(createResource);

  const salesRequestId = useRef(0);
  const productRequestId = useRef(0);
  const userRequestId = useRef(0);

  const loadSales = useCallback(async () => {
    if (!salesScope) return;
    const requestId = ++salesRequestId.current;
    setSales((current) => ({ ...current, isLoading: true, error: null }));
    try {
      const response =
        salesScope === 'all'
          ? await getAllSales({ page: 1, size: 5 })
          : await getMySales({ page: 1, size: 5 });
      if (requestId === salesRequestId.current) {
        setSales({ data: response, isLoading: false, error: null });
      }
    } catch {
      if (requestId === salesRequestId.current) {
        setSales({ data: null, isLoading: false, error: t('admin.dashboard.errors.sales') });
      }
    }
  }, [salesScope, t]);

  const loadProductTotal = useCallback(async () => {
    if (!canReadProducts) return;
    const requestId = ++productRequestId.current;
    setProductTotal((current) => ({ ...current, isLoading: true, error: null }));
    try {
      const response = await getAdminProducts(1, 1);
      if (requestId === productRequestId.current) {
        setProductTotal({ data: response.totalElements, isLoading: false, error: null });
      }
    } catch {
      if (requestId === productRequestId.current) {
        setProductTotal({
          data: null,
          isLoading: false,
          error: t('admin.dashboard.errors.products'),
        });
      }
    }
  }, [canReadProducts, t]);

  const loadUserStatistics = useCallback(async () => {
    if (!canReadUsers) return;
    const requestId = ++userRequestId.current;
    setUserStatistics((current) => ({ ...current, isLoading: true, error: null }));
    try {
      const response = await getUserStatistics();
      if (requestId === userRequestId.current) {
        setUserStatistics({ data: response, isLoading: false, error: null });
      }
    } catch {
      if (requestId === userRequestId.current) {
        setUserStatistics({
          data: null,
          isLoading: false,
          error: t('admin.dashboard.errors.users'),
        });
      }
    }
  }, [canReadUsers, t]);

  useEffect(() => {
    void loadSales();
    return () => {
      salesRequestId.current += 1;
    };
  }, [loadSales]);

  useEffect(() => {
    void loadProductTotal();
    return () => {
      productRequestId.current += 1;
    };
  }, [loadProductTotal]);

  useEffect(() => {
    void loadUserStatistics();
    return () => {
      userRequestId.current += 1;
    };
  }, [loadUserStatistics]);

  return {
    permissions: {
      canCreateSale,
      canReadProducts,
      canReadUsers,
      canReadSales: salesScope !== null,
    },
    salesScope,
    sales,
    productTotal,
    userStatistics,
    retrySales: loadSales,
    retryProductTotal: loadProductTotal,
    retryUserStatistics: loadUserStatistics,
  };
};
